import type { UIMessage } from 'ai';

// Per-message memoization for the Plan Builder / Care Plan Tailor `MessageView`s.
//
// The AI SDK keeps a STABLE object reference for the in-flight assistant message
// and mutates its `parts` in place as tokens stream. So a memo comparator that
// reads the message object can't see changes — `prevProps.message` and
// `nextProps.message` are the same mutated object, every derived value compares
// equal, and the streaming message freezes on its first render.
//
// Mirror the reference (chat-with-patient-data `useChatSession`): derive a fresh
// signature string in the PARENT on every render (an immutable snapshot of the
// message at that moment), pass it as `renderKey`, and compare THAT. A new
// message version yields a different string from the previous render's string,
// so the streaming message re-renders; completed messages keep a stable key and
// bail out of re-rendering.

type MessagePart = UIMessage['parts'][number];

type ToolPart = {
  type: string;
  state?: string;
  input?: unknown;
  output?: unknown;
};

// A cheap per-part signature. Text parts key on their full text (append-only
// while streaming). Tool parts key on state; additionally, the streaming input
// and the settled output are stringified — the live drafting chip derives from
// the partial input, and the final "Draft updated" chip derives from the output
// (validity, issue count, shape), so the signature must change when either does.
function partSignature(part: MessagePart): string {
  if (part.type === 'text') {
    return `t:${part.text}`;
  }
  if (part.type.startsWith('tool-')) {
    const tool = part as ToolPart;
    if (tool.state === 'input-streaming') {
      return `${tool.type}#in:${JSON.stringify(tool.input ?? null)}`;
    }
    if (tool.state === 'output-available') {
      return `${tool.type}#out:${JSON.stringify(tool.output ?? null)}`;
    }
    return `${tool.type}#${tool.state ?? ''}`;
  }
  return part.type;
}

// Compute once per parent render, while the message holds its current values —
// the resulting string is an immutable snapshot safe to compare across renders.
export function messageRenderKey(message: UIMessage): string {
  return message.parts.map(partSignature).join(' ');
}

type MessageViewProps = { busy: boolean; renderKey: string };

export function messageViewPropsEqual(
  prev: MessageViewProps,
  next: MessageViewProps,
): boolean {
  return prev.busy === next.busy && prev.renderKey === next.renderKey;
}
